// ═══════════════════════════════════════════════════════════════════
//  BOOKING REQUEST STATUSES
//
//  The CMS side of a booking's status — what happened at the
//  appointment — as opposed to Cal's own status string on the booking.
//  The API sends these as numbers (no string enum converter), and the
//  numbers are the stored values, so they are mirrored here exactly
//  from AmsBookingRequestStatus in Cms.Services. 8 is retired.
//
//  Which moves a person may make is not decided here: the server sends
//  the allowed list with each booking (AmsBookingStatusView), so the
//  rules live in one place — AmsBookingStatusRules.
// ═══════════════════════════════════════════════════════════════════

import { SITE_TIME_ZONE, longDateLabel, minutesToLabel, zonedDateAndMinutes } from "./siteTime"

export const BookingRequestStatus = {
  Screened: 0,
  Booked: 1,
  Refused: 2,
  Abandoned: 3,
  Expired: 4,
  Cancelled: 5,
  NoShow: 6,
  Rescheduled: 7,
  Complete: 9,
  VoluntaryWithdrawal: 10,
  FailedToProvideDocs: 11,
  Other: 12,
  Incomplete: 13,
  /** Not a real booking — see AmsBookingRequestStatus.Invalid. The status reason says why. */
  Invalid: 14,
  /**
   * Not a valid appointment: CMS's scheduling went wrong and the booking was withdrawn — see
   * AmsBookingRequestStatus.SystemError. The status reason says what went wrong.
   */
  SystemError: 15,
} as const

export type BookingRequestStatus = (typeof BookingRequestStatus)[keyof typeof BookingRequestStatus]

const LABELS: Record<number, string> = {
  [BookingRequestStatus.Screened]: "Screened",
  [BookingRequestStatus.Booked]: "Booked",
  [BookingRequestStatus.Refused]: "Refused",
  [BookingRequestStatus.Abandoned]: "Abandoned",
  [BookingRequestStatus.Expired]: "Expired",
  [BookingRequestStatus.Cancelled]: "Cancelled",
  [BookingRequestStatus.NoShow]: "No show",
  [BookingRequestStatus.Rescheduled]: "Rescheduled",
  // Retired: rows were migrated to Cancelled. Kept so an old number still reads as something.
  8: "Cancelled",
  [BookingRequestStatus.Complete]: "Complete",
  [BookingRequestStatus.VoluntaryWithdrawal]: "Voluntary withdrawal",
  [BookingRequestStatus.FailedToProvideDocs]: "Failed to provide docs",
  [BookingRequestStatus.Other]: "Other",
  [BookingRequestStatus.Incomplete]: "Incomplete",
  [BookingRequestStatus.Invalid]: "Invalid",
  [BookingRequestStatus.SystemError]: "System error",
}

/** A status's name as staff read it. An unknown number is shown as itself rather than hidden. */
export function statusLabel(status: number): string {
  return LABELS[status] ?? `Status ${status}`
}

/**
 * Statuses the system sets to say a booking is not a real appointment, where the status reason is the
 * whole explanation — so it is worth showing on the badge itself, not only in the details below.
 */
export function statusExplainedByReason(status: number): boolean {
  return status === BookingRequestStatus.Invalid || status === BookingRequestStatus.SystemError
}

/** The badge's tooltip: the status reason, for the statuses it explains. */
export function statusTooltip(status: number, statusReason: string | null | undefined): string | undefined {
  if (!statusExplainedByReason(status)) return undefined
  const reason = statusReason?.trim()
  return reason ? reason : undefined
}

/** Statuses that need a reason typed alongside them, whoever sets them. */
export function statusNeedsReason(status: number): boolean {
  return status === BookingRequestStatus.Other || status === BookingRequestStatus.Rescheduled
}

/** What happened at an appointment that took place. */
export function isOutcome(status: number): boolean {
  return status === BookingRequestStatus.Complete
    || status === BookingRequestStatus.NoShow
    || status === BookingRequestStatus.VoluntaryWithdrawal
    || status === BookingRequestStatus.FailedToProvideDocs
    || status === BookingRequestStatus.Other
}

/** A booked appointment nobody has recorded an outcome for yet. Mirrors AmsBookingStatusRules.IsOpen. */
export function isOpenStatus(status: number): boolean {
  return status === BookingRequestStatus.Booked || status === BookingRequestStatus.Incomplete
}

/**
 * Where "Update status" stands for the person looking (F7):
 * - `open`: the server offers statuses — draw the action as usual.
 * - `notYet`: they could record an outcome, but the appointment has not started; draw the action
 *   disabled, saying from when (`opensAt`).
 * - `refreshing`: the start has passed, but the server's list was fetched before it (when it is
 *   always empty), so it says nothing yet. Draw the action disabled while the view is fetched again;
 *   the fresh answer decides. Never an invented list: the server is the authority.
 * - `hidden`: nothing for them to do here.
 */
export type OutcomeGate =
  | { state: "open" }
  | { state: "notYet"; opensAt: Date }
  | { state: "refreshing"; opensAt: Date }
  | { state: "hidden" }

export interface OutcomeGateInput {
  /** The booking's CMS status (AmsBookingRequestStatus). */
  status: number
  /** The server's `allowedStatuses` for the person looking. */
  allowedStatuses: readonly number[]
  /** When an outcome may first be recorded — the slot start (`BookingStatusView.outcomeOpensAt`). */
  outcomeOpensAt?: string | null
  /** Whether the person looking could record an outcome at all: they administer appointments, or host this one. */
  mayUpdate: boolean
  /**
   * When the status view carrying `allowedStatuses` was fetched (epoch ms; RTK Query's
   * `fulfilledTimeStamp`). Unknown means the list is taken as current.
   */
  viewFetchedAt?: number | null
}

/**
 * How long after the start an empty list still counts as possibly stale: the browser's clock and the
 * server's need not agree, so a view fetched a moment "after" the start by this clock may still have
 * been answered before it by the server's.
 */
export const OUTCOME_REFRESH_GRACE_MS = 5_000

/** How long to wait between refetches while an empty list is still within the grace. */
export const OUTCOME_REFRESH_RETRY_MS = 2_000

/**
 * Whether "Update status" is offered, withheld until the appointment starts, being re-checked, or not
 * shown.
 *
 * The server still decides: a non-empty `allowedStatuses` is always `open`, and the time check only
 * explains an empty one. It mirrors AmsBookingStatusRules.Evaluate, which refuses an outcome while
 * `start > now` — so at exactly the start time the outcome is open.
 */
export function outcomeGate(input: OutcomeGateInput, now: Date): OutcomeGate {
  if (input.allowedStatuses.length > 0) return { state: "open" }
  if (!input.mayUpdate || !isOpenStatus(input.status) || !input.outcomeOpensAt) return { state: "hidden" }

  const opensAt = new Date(input.outcomeOpensAt)
  if (Number.isNaN(opensAt.getTime())) return { state: "hidden" }

  if (opensAt.getTime() > now.getTime()) return { state: "notYet", opensAt }

  // Past the start with nothing offered. A list fetched before the start (give or take the clocks'
  // disagreement) was empty only because it was early: it is stale, not a refusal, so the action
  // stays drawn until a fresh one arrives rather than vanishing at the very moment it should open.
  const fetchedAt = input.viewFetchedAt
  if (fetchedAt !== undefined && fetchedAt !== null && fetchedAt < opensAt.getTime() + OUTCOME_REFRESH_GRACE_MS) {
    return { state: "refreshing", opensAt }
  }

  // Fetched after the start and still empty: the server has its reasons. Nothing to explain.
  return { state: "hidden" }
}

/**
 * When (epoch ms) the status view should next be fetched for the gate to move on, or undefined when
 * nothing will change by waiting: at the start for `notYet`; for `refreshing`, now if the view
 * predates the start, else shortly after the last fetch until the grace is over.
 */
export function nextOutcomeRefresh(gate: OutcomeGate, viewFetchedAt?: number | null): number | undefined {
  if (gate.state === "notYet") return gate.opensAt.getTime()
  if (gate.state !== "refreshing") return undefined

  const opensAt = gate.opensAt.getTime()
  if (viewFetchedAt === undefined || viewFetchedAt === null || viewFetchedAt < opensAt) return opensAt
  return viewFetchedAt + OUTCOME_REFRESH_RETRY_MS
}

/** What the disabled action says while the fresh status view is on its way. */
export const OUTCOME_REFRESHING_MESSAGE = "Checking whether the outcome can be recorded now"

/** "You can record the outcome from 10:30 AM on Friday, Oct 2 (Pacific)". The site's clock, not the browser's. */
export function outcomeOpensMessage(opensAt: Date, timeZone: string = SITE_TIME_ZONE): string {
  const { date, minutes } = zonedDateAndMinutes(opensAt, timeZone)
  const zone = timeZone === SITE_TIME_ZONE ? "Pacific" : timeZone
  return `You can record the outcome from ${minutesToLabel(minutes)} on ${longDateLabel(date)} (${zone})`
}

/**
 * Whether moving from `from` to `to` changes an outcome somebody already recorded — which the
 * server only lets an administrator do, and only with a reason.
 */
export function isCorrection(from: number, to: number): boolean {
  return isOutcome(from) && from !== to
}

/** A badge colour for the status, so a table of them can be scanned. */
export function statusTone(status: number): "positive" | "negative" | "attention" | "neutral" {
  switch (status) {
    case BookingRequestStatus.Complete:
      return "positive"
    case BookingRequestStatus.NoShow:
    case BookingRequestStatus.Cancelled:
    case BookingRequestStatus.FailedToProvideDocs:
    case BookingRequestStatus.Refused:
    case BookingRequestStatus.Invalid:
    case BookingRequestStatus.SystemError:
      return "negative"
    case BookingRequestStatus.Incomplete:
      return "attention"
    default:
      return "neutral"
  }
}

/** Tailwind classes for `statusTone`, applied over the shadcn Badge's outline variant. */
export function statusBadgeClass(status: number): string {
  switch (statusTone(status)) {
    case "positive":
      return "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200"
    case "negative":
      return "border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
    case "attention":
      return "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200"
    default:
      return ""
  }
}

/** Mirrors AmsBookingActor. */
const ACTORS: Record<number, string> = {
  0: "Unknown",
  1: "Attendee",
  2: "Staff",
  3: "System",
}

/** Who cancelled, as a word. */
export function actorLabel(actor: number | null | undefined): string | undefined {
  return actor === null || actor === undefined ? undefined : ACTORS[actor] ?? undefined
}
